import { useState, useEffect } from "react";
import axios from "axios";
import InstitutionalSections from "./InstitutionalSections";
import DownloadAppButton from "./DownloadAppButton";
import LoginCommercial from "./LoginCommercial";
import { extractApiError } from "../utils/apiError";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function LoginPage({ onLogin, config, onShowPrivacy }) {
  const [isRegister, setIsRegister] = useState(false);
  const [formData, setFormData] = useState({
    username: "",
    password: "",
    email: ""
  });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [forgotPasswordEmail, setForgotPasswordEmail] = useState("");
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [showResetForm, setShowResetForm] = useState(false);
  const [resetToken, setResetToken] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");

  // Login-page commercial: OPT-IN. Only shown when the user explicitly
  // clicks "Watch Tour". Previously auto-played on first visit, but
  // students reported the tutorial blocked login on mobile and Skip
  // sometimes fell under the chip overflow. Now it never launches on
  // its own — see the "Watch Tour" button in the hero.
  const [showCommercial, setShowCommercial] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);

    // ── 2026-02-21 hardening (iter 60) ─────────────────────────
    // The original 15 s timeout + 1 retry was killing mobile users on
    // slow LTE (the earlier rerender storm wedged the origin and the
    // subsequent recovery cold-start took ~18-25 s). Fix:
    //   1. Bump per-attempt timeout to 30 s.
    //   2. Retry up to 3 times (was 2) with exponential backoff
    //      (0.9 s → 2 s → 4 s) so a transient blip never dead-ends.
    //   3. Retry on ECONNABORTED (timeouts) AND on empty-response
    //      network errors, not just 5xx.
    //   4. On the FINAL failure show a message the user can act on.
    const endpoint = isRegister ? "/auth/register" : "/auth/login";
    const payload = isRegister
      ? formData
      : { username: (formData.username || "").trim(), password: formData.password };

    const attempt = () => axios.post(`${API}${endpoint}`, payload, { timeout: 30000 });
    const MAX_ATTEMPTS = 3;
    const BACKOFFS = [900, 2000, 4000];

    let response = null;
    let lastErr = null;
    for (let i = 0; i < MAX_ATTEMPTS; i++) {
      try {
        response = await attempt();
        break;
      } catch (err) {
        lastErr = err;
        const status = err?.response?.status;
        // 4xx = don't retry (wrong password / bad payload).
        if (status && status >= 400 && status < 500) break;
        // Everything else (timeout, network, 5xx) → back off + retry.
        if (i < MAX_ATTEMPTS - 1) {
          await new Promise((r) => setTimeout(r, BACKOFFS[i]));
        }
      }
    }

    if (response) {
      try {
        onLogin(response.data.user, response.data.access_token);
      } catch (renderErr) {
        setError("Signed in, but the app couldn't finish loading. Try refreshing.");
      }
      setLoading(false);
      return;
    }

    // No response — build a helpful message
    const raw = lastErr?.response?.data?.detail;
    let message;
    if (typeof raw === "string") {
      message = raw;
    } else if (Array.isArray(raw)) {
      message = raw.map((d) => d?.msg || JSON.stringify(d)).join("; ");
    } else if (lastErr?.code === "ECONNABORTED") {
      message = "Login is taking longer than expected. Please check your connection and tap Login again — we'll try one more time.";
    } else if (!lastErr?.response) {
      message = "We can't reach the server right now. Please check your internet connection or try again in a moment.";
    } else if (lastErr?.response?.status >= 500) {
      message = "Server error — please try again shortly.";
    } else {
      message = "Sign-in failed. Please double-check your username and password.";
    }
    setError(message);
    setLoading(false);
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setError("");
    setSuccessMessage("");
    setLoading(true);

    try {
      const response = await axios.post(`${API}/auth/forgot-password?email=${encodeURIComponent(forgotPasswordEmail)}`);
      if (response.data.success) {
        setSuccessMessage(response.data.message || "Password recovery instructions have been sent. Please check your email.");
        // Show the reset form after email is sent
        setShowResetForm(true);
      } else {
        setSuccessMessage("If an account exists with this email, password reset instructions have been sent.");
      }
    } catch (err) {
      setError(extractApiError(err, "Error sending password reset. Please try again."));
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    setError("");
    setSuccessMessage("");

    if (newPassword !== confirmPassword) {
      setError("Passwords do not match");
      return;
    }

    if (newPassword.length < 8) {
      setError("Password must be at least 8 characters");
      return;
    }

    setLoading(true);

    try {
      const response = await axios.post(`${API}/auth/reset-password`, {
        token: resetToken.trim(),
        new_password: newPassword
      });
      
      if (response.data.success) {
        setSuccessMessage("Password reset successful! You can now login with your new password.");
        setShowForgotPassword(false);
        setShowResetForm(false);
        setResetToken("");
        setNewPassword("");
        setConfirmPassword("");
        setForgotPasswordEmail("");
      }
    } catch (err) {
      setError(extractApiError(err, "Error resetting password. Please try again."));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="landing-page">
      {showCommercial && (
        <LoginCommercial
          onClose={() => setShowCommercial(false)}
          onBeginJourney={() => {
            setIsRegister(true);
            setTimeout(
              () =>
                document
                  .getElementById("auth-section")
                  ?.scrollIntoView({ behavior: "smooth" }),
              300
            );
          }}
        />
      )}
      {/* Enhanced Header with Logo */}
      <header className="app-header enhanced-header">
        <div className="header-top-bar">
          <span>orgof12tribe@gmail.com</span>
          <span>|</span>
          <span>www.ylcenter.com</span>
        </div>
        <div className="header-main">
          {/* YLC Official Logo */}
          <div className="header-logo">
            <img 
              src="/ylc-logo.png" 
              alt="Yisrael Learning Center Logo"
              className="header-logo-img"
            />
          </div>
          <h1>Yisrael Learning Center</h1>
          <p className="header-subtitle">{config?.CHURCH_NAME || "Twelve Tribes of Yisrael"}</p>
          <div className="header-badges">
            <span className="badge">Est. {config?.ESTABLISHED || 2012}</span>
            <span className="badge">Chief/Priest: {config?.CHIEF_PRIEST || "Ykbn Yoonw"}</span>
          </div>
        </div>
        <p className="header-tagline">
          Official Educational Institution of the Twelve Tribes of Yisrael
        </p>
        <p className="header-programs">
          K–12 Education • Scriptural Studies • Ministry Training • Ordination Program
        </p>
      </header>

      {/* SAFEGUARD: Hero Section - Industry Standard Layout
          - hero-container provides centered max-width wrapper
          - hero-content centers all text content
          - hero-title MUST be white (#FFFFFF) for visibility
          - hero-subtitle MUST be light gray (#CCC) for readability
          DO NOT use dark colors on dark backgrounds! */}
      <section className="hero-section">
        <div className="hero-container">
          <div className="hero-content">
            <h2 className="hero-title">Restoring Knowledge. Teaching the Scriptures. All are Welcome.</h2>
            <p className="hero-subtitle">
              Yisrael Learning Center is a global educational institution dedicated to restoring the knowledge, heritage, and teachings found in the Holy Scriptures. Through structured learning and disciplined study, students gain a deeper understanding of the history, laws, and wisdom given to the Nation of Yisrael.
            </p>
            <p className="hero-subtitle-secondary">
              This center is open to people of every nation who desire truth, knowledge, and spiritual growth. Begin your journey with a 7-day free trial, with full access to all teachings available for $19.00 USD per month.
            </p>
            <div className="hero-cta">
              <button 
                className="cta-primary"
                data-testid="begin-journey-btn"
                onClick={() => {
                  setIsRegister(true);
                  document.getElementById('auth-section')?.scrollIntoView({ behavior: 'smooth' });
                }}
              >
                Begin Your Journey
              </button>
              <button 
                className="cta-secondary"
                data-testid="explore-programs-btn"
                onClick={() => document.getElementById('programs-section')?.scrollIntoView({ behavior: 'smooth' })}
              >
                Explore Programs
              </button>
              <button
                className="cta-secondary"
                data-testid="watch-tour-btn"
                onClick={() => setShowCommercial(true)}
                style={{
                  background:
                    "linear-gradient(90deg, rgba(255,215,0,0.15), rgba(255,165,0,0.15))",
                  border: "1px solid #FFD70080",
                  color: "#FFD700",
                }}
              >
                ▶︎ Watch Tour
              </button>
            </div>
            
            {/* Download App Button */}
            <div className="hero-download">
              <DownloadAppButton variant="hero" />
              <p className="download-hint">Available on iOS, Android & Desktop</p>
            </div>
          </div>
        </div>
      </section>

      <div className="container" id="auth-section">
        {!showForgotPassword ? (
          <div className="card" style={{ maxWidth: "500px", margin: "40px auto" }}>
            <h2 data-testid="login-title">{isRegister ? "Create Account" : "Student Login"}</h2>

            {error && <div className="alert alert-error" data-testid="error-message">{error}</div>}

            <form onSubmit={handleSubmit} data-testid="login-form">
              <input
                data-testid="username-input"
                type="text"
                placeholder={isRegister ? "Username" : "Username or Email"}
                value={formData.username}
                onChange={(e) =>
                  setFormData({ ...formData, username: e.target.value })
                }
                required
              />

              {isRegister && (
                <input
                  data-testid="email-input"
                  type="email"
                  placeholder="Email"
                  value={formData.email}
                  onChange={(e) =>
                    setFormData({ ...formData, email: e.target.value })
                  }
                  required
                />
              )}

              <input
                data-testid="password-input"
                type="password"
                placeholder={formData.username?.toUpperCase() === "YKBN" ? "Password (optional for admin)" : "Password"}
                value={formData.password}
                onChange={(e) =>
                  setFormData({ ...formData, password: e.target.value })
                }
                required={isRegister || formData.username?.toUpperCase() !== "YKBN"}
              />

              <button data-testid="submit-button" type="submit" disabled={loading}>
                {loading ? "Processing..." : isRegister ? "Create Account" : "Login"}
              </button>
            </form>

            {/* ── Emergent-managed Google Sign-In ─────────────────────
                REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS
                OR REDIRECT URLS, THIS BREAKS THE AUTH.
                Derived from `window.location.origin` so it always
                returns to the exact domain the visitor came from. */}
            <div style={{
              display: "flex", alignItems: "center", gap: 12,
              margin: "18px 0 10px",
              color: "#888", fontSize: "0.8rem",
            }}>
              <div style={{ flex: 1, height: 1, background: "#333" }} />
              <span>or</span>
              <div style={{ flex: 1, height: 1, background: "#333" }} />
            </div>
            <button
              type="button"
              data-testid="google-signin-btn"
              onClick={() => {
                const redirectUrl = window.location.origin + "/";
                window.location.href =
                  "https://auth.emergentagent.com/?redirect=" +
                  encodeURIComponent(redirectUrl);
              }}
              style={{
                width: "100%",
                display: "flex", alignItems: "center", justifyContent: "center",
                gap: 10, padding: "12px 16px", borderRadius: 8,
                background: "#fff", color: "#111",
                border: "1px solid #dadce0", cursor: "pointer",
                fontFamily: "Roboto, Arial, sans-serif", fontSize: "0.95rem",
                fontWeight: 500,
              }}
            >
              <svg width="20" height="20" viewBox="0 0 48 48" aria-hidden="true">
                <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/>
                <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/>
                <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/>
                <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/>
              </svg>
              Continue with Google
            </button>

            {isRegister && (
              <div className="alert alert-info" style={{ marginTop: "15px" }}>
                <p style={{ margin: "0 0 8px 0", fontSize: "0.9rem" }}>
                  <strong>Subscription: ${config?.SUBSCRIPTION_PRICE || 19}/month</strong>
                </p>
                <p style={{ margin: "0", fontSize: "0.85rem" }}>
                  Unlimited access to all courses, exams, and ministry training
                </p>
              </div>
            )}

            <p style={{ marginTop: "20px", textAlign: "center" }}>
              {isRegister ? (
                <>
                  Already have an account?{" "}
                  <a data-testid="toggle-register-link" onClick={() => setIsRegister(false)}>
                    Login here
                  </a>
                </>
              ) : (
                <>
                  Don't have an account?{" "}
                  <a data-testid="toggle-login-link" onClick={() => setIsRegister(true)}>
                    Create account
                  </a>
                </>
              )}
            </p>

            {!isRegister && (
              <p style={{ textAlign: "center", marginTop: "10px" }}>
                <a data-testid="forgot-password-link" onClick={() => setShowForgotPassword(true)}>
                  Forgot Password?
                </a>
              </p>
            )}
          </div>
        ) : (
          <div className="card" style={{ maxWidth: "500px", margin: "40px auto" }}>
            <h2>{showResetForm ? "Reset Your Password" : "Password Recovery"}</h2>

            {error && <div className="alert alert-error">{error}</div>}
            {successMessage && (
              <div style={{
                background: "#1a4d1a",
                border: "1px solid #90EE90",
                color: "#90EE90",
                padding: "15px",
                borderRadius: "8px",
                marginBottom: "15px"
              }}>
                {successMessage}
              </div>
            )}

            {!showResetForm ? (
              <>
                <form onSubmit={handleForgotPassword}>
                  <input
                    type="email"
                    placeholder="Enter your email"
                    value={forgotPasswordEmail}
                    onChange={(e) => setForgotPasswordEmail(e.target.value)}
                    required
                  />

                  <button type="submit" disabled={loading}>
                    {loading ? "Sending..." : "Send Reset Email"}
                  </button>
                </form>

                <div style={{ 
                  marginTop: "20px", 
                  padding: "15px", 
                  background: "#1a3a4d", 
                  borderRadius: "6px",
                  border: "1px solid #87CEEB"
                }}>
                  <p style={{ margin: "0 0 8px 0", fontSize: "0.9rem", color: "#87CEEB" }}>
                    <strong>Password Reset Support</strong>
                  </p>
                  <p style={{ margin: "0", fontSize: "0.85rem", color: "#eee" }}>
                    Password recovery emails are sent from:
                  </p>
                  <p style={{ 
                    margin: "8px 0 0 0", 
                    fontSize: "0.9rem", 
                    color: "#FFD700",
                    fontWeight: "bold" 
                  }}>
                    orgof12tribe@gmail.com
                  </p>
                </div>

                <p style={{ textAlign: "center", marginTop: "15px" }}>
                  <a onClick={() => setShowResetForm(true)} style={{ cursor: "pointer" }}>
                    Already have a reset token? Click here
                  </a>
                </p>
              </>
            ) : (
              <>
                <p style={{ color: "#aaa", marginBottom: "15px" }}>
                  Enter the reset token from your email and create a new password.
                </p>
                
                <form onSubmit={handleResetPassword}>
                  <input
                    type="text"
                    placeholder="Enter reset token from email"
                    value={resetToken}
                    onChange={(e) => setResetToken(e.target.value)}
                    required
                    style={{ fontFamily: "monospace" }}
                  />

                  <input
                    type="password"
                    placeholder="New password (min 8 characters)"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    required
                    minLength={8}
                  />

                  <input
                    type="password"
                    placeholder="Confirm new password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    required
                    minLength={8}
                  />

                  <button type="submit" disabled={loading}>
                    {loading ? "Resetting..." : "Reset Password"}
                  </button>
                </form>

                <p style={{ textAlign: "center", marginTop: "15px" }}>
                  <a onClick={() => setShowResetForm(false)} style={{ cursor: "pointer" }}>
                    Need a reset token? Go back
                  </a>
                </p>
              </>
            )}

            <p style={{ textAlign: "center", marginTop: "20px" }}>
              <a onClick={() => {
                setShowForgotPassword(false);
                setShowResetForm(false);
                setError("");
                setSuccessMessage("");
              }}>Back to Login</a>
            </p>
          </div>
        )}
      </div>

      {/* Institutional Sections */}
      <div id="programs-section">
        <InstitutionalSections config={config} onShowPrivacy={onShowPrivacy} />
      </div>

      {/* Enhanced Landing Page Styles */}
      <style>{`
        .landing-page {
          min-height: 100vh;
        }
        
        .enhanced-header {
          background: linear-gradient(180deg, #0a0a0a 0%, #0f0f1a 100%);
          padding: 0;
          text-align: center;
        }
        
        .header-top-bar {
          background: rgba(255, 215, 0, 0.08);
          padding: 10px 20px;
          font-size: 0.85rem;
          color: #888;
          display: flex;
          justify-content: center;
          gap: 20px;
          border-bottom: 1px solid rgba(255, 215, 0, 0.15);
        }
        
        .header-main {
          padding: 50px 20px 30px;
          display: flex;
          flex-direction: column;
          align-items: center;
        }
        
        .header-logo {
          margin-bottom: 20px;
        }
        
        .header-logo-img {
          width: 100px;
          height: auto;
          border-radius: 10px;
        }
        
        .header-main h1 {
          font-family: 'Playfair Display', serif;
          font-size: 4.5rem;
          color: #FFD700;
          margin: 0;
          text-shadow: 0 2px 30px rgba(255, 215, 0, 0.25);
          letter-spacing: 2px;
          width: 100%;
          text-align: center;
        }
        
        @media (max-width: 768px) {
          .header-main h1 {
            font-size: 2.8rem;
            letter-spacing: 1px;
          }
          .header-logo-img {
            width: 80px;
          }
        }
        
        @media (max-width: 480px) {
          .header-main h1 {
            font-size: 2rem;
            letter-spacing: 0.5px;
          }
        }
        
        .header-subtitle {
          color: #87CEEB;
          font-size: 1.3rem;
          margin: 15px 0;
          font-weight: 500;
        }
        
        .header-badges {
          display: flex;
          justify-content: center;
          gap: 15px;
          margin-top: 20px;
          flex-wrap: wrap;
        }
        
        .header-badges .badge {
          background: rgba(255, 215, 0, 0.1);
          border: 1px solid rgba(255, 215, 0, 0.25);
          color: #FFD700;
          padding: 8px 18px;
          border-radius: 25px;
          font-size: 0.9rem;
          font-weight: 500;
        }
        
        .header-tagline {
          color: #fff;
          font-size: 1.15rem;
          margin: 30px 0 8px;
          font-weight: 500;
        }
        
        .header-programs {
          color: #999;
          font-size: 0.95rem;
          margin: 0;
          padding-bottom: 40px;
          letter-spacing: 0.5px;
        }
        
        .hero-section {
          background: linear-gradient(180deg, #1a1a2e 0%, #0f0f1a 50%, #0a0a0a 100%);
          padding: 80px 20px 100px;
          display: flex;
          align-items: center;
          justify-content: center;
          min-height: 60vh;
        }
        
        .hero-container {
          width: 100%;
          max-width: 1200px;
          margin: 0 auto;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
        }
        
        .hero-content {
          width: 100%;
          max-width: 900px;
          text-align: center;
          display: flex;
          flex-direction: column;
          align-items: center;
        }
        
        .hero-title {
          font-family: 'Playfair Display', serif;
          font-size: 3rem;
          color: #FFFFFF;
          margin: 0 0 30px 0;
          line-height: 1.2;
          font-weight: 700;
          letter-spacing: -0.5px;
          text-align: center;
          width: 100%;
        }
        
        @media (max-width: 768px) {
          .hero-title {
            font-size: 2.2rem;
          }
          .hero-section {
            padding: 60px 20px 80px;
            min-height: auto;
          }
        }
        
        @media (max-width: 480px) {
          .hero-title {
            font-size: 1.8rem;
          }
        }
        
        .hero-subtitle {
          color: #CCCCCC;
          font-size: 1.15rem;
          line-height: 1.8;
          margin: 0 0 20px 0;
          max-width: 750px;
          text-align: center;
        }
        
        .hero-subtitle-secondary {
          color: #AAAAAA;
          font-size: 1.05rem;
          line-height: 1.7;
          margin: 0 0 40px 0;
          max-width: 700px;
          text-align: center;
        }
        
        .hero-cta {
          display: flex;
          justify-content: center;
          align-items: center;
          gap: 20px;
          flex-wrap: wrap;
          margin-top: 10px;
        }
        
        .cta-primary {
          background: linear-gradient(135deg, #FFD700 0%, #FFA500 100%);
          color: #000;
          border: none;
          padding: 18px 45px;
          font-size: 1rem;
          font-weight: 700;
          border-radius: 50px;
          cursor: pointer;
          transition: all 0.3s ease;
          margin: 0;
          text-transform: uppercase;
          letter-spacing: 1px;
          box-shadow: 0 4px 20px rgba(255, 215, 0, 0.3);
          min-width: auto;
          width: auto;
        }
        
        .cta-primary:hover {
          transform: translateY(-3px);
          box-shadow: 0 10px 30px rgba(255, 215, 0, 0.4);
        }
        
        .cta-secondary {
          background: transparent;
          color: #FFD700;
          border: 2px solid #FFD700;
          padding: 16px 40px;
          font-size: 1rem;
          font-weight: 600;
          border-radius: 50px;
          cursor: pointer;
          transition: all 0.3s ease;
          margin: 0;
          letter-spacing: 0.5px;
          min-width: auto;
          width: auto;
        }
        
        .cta-secondary:hover {
          background: rgba(255, 215, 0, 0.1);
          transform: translateY(-2px);
        }
        
        .hero-download {
          margin-top: 40px;
          text-align: center;
        }
        
        .download-hint {
          color: #999;
          font-size: 0.85rem;
          margin-top: 12px;
        }

        /* Header Logo Styles */
        .header-logo {
          margin-bottom: 15px;
        }
        
        .header-logo-img {
          width: 120px;
          height: auto;
          margin: 0 auto;
          display: block;
          border-radius: 10px;
        }

        @media (max-width: 768px) {
          .header-logo-img {
            width: 100px;
          }
        }
      `}</style>
    </div>
  );
}

export default LoginPage;